import {
  DispatchPageType,
  DispatchPreviewSaveInput,
  DispatchScope,
  DispatchSource,
  DispatchStopsOnDayInput,
  Driver,
  OrderingAlgorithmType,
  ShipmentStopType,
  Stop,
  useDispatchDriverForShipmentMultiDayMutation,
  useDispatchDriverForShipmentMutation,
  useGetShipmentAllStopDatesForGroupDispatchQuery,
} from "@api/graphql/generated/generated-types";
import { DriverAvatar } from "@src/common/components/Avatar";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
} from "@src/common/components/BaseModal";
import CourieButton from "@src/common/components/Button/Button";
import { ToggleSwitch } from "@src/common/components";
import ChangeDriverRouteView, {
  RouteViewMode,
} from "@src/common/components/ChangeDriverRouteView/ChangeDriverRouteView";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { ShipmentRow } from "@src/shipments/types";
import { SIZE } from "baseui/modal";
import React, { useCallback, useEffect, useState } from "react";
import {
  getDispatchScope,
  getStopIds,
  getUniqueStopDates,
  pickStopDatesForDispatch,
} from "../utils/assignDriverModalUtils";
import StopDateListTimeline from "@src/common/components/ChangeDriverRouteView/components/StopDateListTimeline";
import { findIfShipmentIsMultiDay } from "@src/common/lib/shipmentStateFinders";
import { useAuth } from "@src/common/hooks/useAuth";
import { useDriverComplianceGate } from "@src/driverCompliancePolicies";
import { DriverComplianceWarningContent } from "@src/driverCompliancePolicies/components/DriverComplianceWarningContent";
import { useLocalStorage } from "usehooks-ts";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedRow: ShipmentRow;
  selectedDriver: Driver | undefined;
  setSelectedDriver: (driver: Driver | undefined) => void;
  submitButtonLabel?: string;
  onConfirm?: (shouldConfigureAllocation?: boolean) => void;
  dispatchPreviewSortingType?: string | undefined;
  dispatchPreviews?:
    | DispatchPreviewSaveInput
    | DispatchPreviewSaveInput[]
    | undefined;
  driverRank?: number | undefined;
  dispatchPreviewSection?: string | null;
  routeViewMode?: RouteViewMode;
  onDispatch?: (lastChangedShipmentId?: string | null | undefined) => void;
  resetKeyboardStates?: () => void;
  dispatchPageType?: DispatchPageType;
  onAssignAndSchedule?: () => void;
};

function AssignDriverModal({
  isOpen,
  setIsOpen,
  selectedRow,
  selectedDriver,
  setSelectedDriver,
  submitButtonLabel = "Assign",
  onConfirm,
  dispatchPreviewSortingType,
  dispatchPreviews,
  driverRank,
  dispatchPreviewSection,
  routeViewMode = RouteViewMode.Dispatch,
  onDispatch,
  resetKeyboardStates,
  dispatchPageType,
  onAssignAndSchedule,
}: Props) {
  // context
  const { showToast } = useCourieStore();
  const { courierId, courieUser, employeeId } = useAuth();
  // mutation
  const [
    dispatchDriverForShipmentMutation,
    { loading: DispatchDriverForShipmentMutationLoading },
  ] = useDispatchDriverForShipmentMutation();
  const [
    dispatchDriverForShipmentMultiDayMutation,
    { loading: DispatchDriverForShipmentMultiDayMutationLoading },
  ] = useDispatchDriverForShipmentMultiDayMutation();
  // states
  const [routeDetails, setRouteDetails] =
    useState<Stop[] | undefined>(undefined);
  const [isValidOrder, setIsValidOrder] = useState(true);
  // const [selectedOrderingAlgorithmType, setSelectedOrderingAlgorithmType] =
  //   useState(OrderingAlgorithmType.Manual);
  const [autoRoutedStopIds, setAutoRoutedStopIds] =
    useState<undefined | string[]>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [listStopDates, setListStopDates] = useState<string[]>([]);
  const [selectedStopDate, setSelectedStopDate] = useState("");
  const [daysGrouped, setDaysGrouped] = useState<{
    [date: string]: DispatchStopsOnDayInput;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const shouldScheduleAfterAssignRef = React.useRef(false);
  // Read at dispatch time via ref: executeDispatch's useCallback would
  // otherwise capture a stale toggle value from an earlier render.
  const shouldConfigureAllocationRef = React.useRef(true);

  const [isScheduleNextStop, setIsScheduleNextStop] = useLocalStorage(
    "courie:scheduleNextStop",
    false,
  );
  const [shouldConfigureAllocation, setShouldConfigureAllocation] =
    useLocalStorage("courie:configureAllocation", true);
  const [selectedOrderingAlgorithmType, setSelectedOrderingAlgorithmType] =
    useState<OrderingAlgorithmType | undefined>(
      OrderingAlgorithmType.LeastDeviation,
    );
  const {
    check: complianceCheck,
    loading: isCheckingCompliance,
    showWarning,
    warningTypes,
    closeWarning,
    proceedFromWarning,
  } = useDriverComplianceGate();

  // Only an order in an assignment group pays for the group lookups; every other order dispatches
  // as a single order, as before group dispatch. A row without the field counts as ungrouped.
  const dispatchScope = getDispatchScope(
    routeViewMode,
    selectedRow?.shipment?.order?.orderGroupOverview?.assignmentGroupId,
  );
  const isGroupScope = dispatchScope === DispatchScope.AssignmentGroup;
  // The group's days come from cube: another order of the group can have stops on a day this order has none.
  // A failed or empty answer falls back to this order's own days.
  const {
    data: groupStopDatesData,
    loading: isGroupStopDatesLoading,
  } = useGetShipmentAllStopDatesForGroupDispatchQuery({
    variables: { courierId: courierId ?? "", shipmentId: selectedRow?.id ?? "" },
    skip: !isGroupScope || !isOpen || !courierId || !selectedRow?.id,
    fetchPolicy: "no-cache",
  });
  const groupStopDates: string[] | undefined = isGroupScope
    ? groupStopDatesData?.shipmentById.allStopDatesForGroupDispatch
    : undefined;
  const isWaitingForGroupStopDates = isGroupScope && isGroupStopDatesLoading;

  const isLastStopDate =
    listStopDates === undefined ||
    listStopDates.length === 0 ||
    listStopDates.indexOf(selectedStopDate) === listStopDates.length - 1;

  const memoizedRouteDetails = React.useMemo(() => {
    return routeDetails;
  }, [routeDetails]);

  const memoizedSelectedRow = React.useMemo(() => {
    return selectedRow;
  }, [selectedRow?.id]);

  const previousSelectedRowIdRef = React.useRef<string | undefined>(
    selectedRow?.id,
  );

  function resetDayState() {
    setListStopDates([]);
    setDaysGrouped({});
    setSelectedStopDate("");
    setAutoRoutedStopIds(undefined);
  }

  // A mounted modal (Change Driver mode) can receive a new order without ever closing. Reset the
  // per-order day state whenever the selected order actually changes, so a later order's dates never
  // inherit the previous order's listStopDates / daysGrouped. Declared before the effects below so the
  // reset always runs first in the commit where the order changes.
  useEffect(() => {
    const previousSelectedRowId = previousSelectedRowIdRef.current;
    previousSelectedRowIdRef.current = selectedRow?.id;
    if (previousSelectedRowId && previousSelectedRowId !== selectedRow?.id) {
      resetDayState();
    }
  }, [selectedRow?.id]);

  useEffect(() => {
    if (!selectedRow) return;
    // A group spread over several days is multi-day even when this order's own stops are on one day.
    const isMultidayShipment =
      listStopDates.length > 1 ||
      (selectedRow.shipment &&
        findIfShipmentIsMultiDay(
          selectedRow.shipment.stops,
          selectedRow.shipment.shipmentDate,
        ));
    if (
      isMultidayShipment &&
      memoizedRouteDetails &&
      memoizedRouteDetails.length > 0
    ) {
      const newDaysGrouped = groupStopsByDate(
        memoizedRouteDetails as Stop[],
        selectedStopDate,
        autoRoutedStopIds,
      );
      // Right after Next/Back the route still holds the previous day's stops; only save stops that
      // belong to the selected date, so a date is never stored with another date's route.
      const selectedDay = newDaysGrouped?.find(
        (day) => day.stopDate === selectedStopDate,
      );
      selectedDay &&
        setDaysGrouped((prevDays) => ({
          ...prevDays,
          [selectedStopDate]: selectedDay,
        }));
    }
  }, [memoizedRouteDetails, selectedStopDate, listStopDates]);

  // effects
  useEffect(() => {
    if (!selectedDriver) {
      return;
    }
    if (isWaitingForGroupStopDates) {
      return;
    }
    const orderStops = memoizedSelectedRow?.shipment?.stops || [];
    const uncompletedStops = orderStops.filter((stop) => !stop.completedAt);
    const routeDetails = uncompletedStops.sort((a, b) => {
      if (
        a.type === ShipmentStopType.PickUp &&
        b.type !== ShipmentStopType.PickUp
      )
        return -1;
      if (
        a.type !== ShipmentStopType.PickUp &&
        b.type === ShipmentStopType.PickUp
      )
        return 1;
      return 0;
    });
    const listStopDates: undefined | string[] = pickStopDatesForDispatch(
      groupStopDates,
      getUniqueStopDates(routeDetails),
    );
    let routeDetailsForSelectedDate: Stop[] = [];
    if (listStopDates && listStopDates.length > 1) {
      setListStopDates(listStopDates);
      if (!selectedStopDate) {
        setSelectedStopDate(listStopDates[0]);
      }

      routeDetailsForSelectedDate = routeDetails.filter((routeDetail) => {
        const stopDate = routeDetail.effectiveStopDate;
        return stopDate === selectedStopDate;
      });
    } else {
      const shipmentDate = memoizedSelectedRow?.shipment?.shipmentDate;
      if (listStopDates && shipmentDate !== listStopDates[0]) {
        setSelectedStopDate(listStopDates[0]);
      } else {
        setSelectedStopDate(shipmentDate);
      }
      routeDetailsForSelectedDate = routeDetails;
    }
    setRouteDetails(routeDetailsForSelectedDate);
  }, [
    selectedDriver,
    memoizedSelectedRow,
    selectedStopDate,
    groupStopDates,
    isWaitingForGroupStopDates,
  ]);

  const executeDispatch = useCallback(() => {
    if (
      !selectedRow ||
      !selectedDriver ||
      !selectedOrderingAlgorithmType ||
      !courierId
    ) {
      return;
    }
    const isMultidayShipment =
      selectedRow.shipment &&
      findIfShipmentIsMultiDay(
        selectedRow.shipment.stops,
        selectedRow.shipment.shipmentDate,
      );
    if (listStopDates.length > 1 || isMultidayShipment) {
      const days = Object.values(daysGrouped);
      dispatchDriverForShipmentMultiDayMutation({
        variables: {
          courierId: courierId,
          shipmentId: selectedRow.id,
          driverId: selectedDriver.id,
          orderingAlgorithmType: selectedOrderingAlgorithmType,
          dispatchedBy: employeeId ? employeeId : courieUser!.id,
          dispatchSource: DispatchSource.Dispatcher,
          days,
          dispatchPreviewSortingType,
          dispatchPreviews: dispatchPreviews,
          driverRank,
          dispatchPreviewSection,
          dispatchPageType,
          dispatchScope,
        },
      })
        .then(() => {
          handleSuccessDispatchResponse();
        })
        .catch((error) => {
          shouldScheduleAfterAssignRef.current = false;
          showErrorToast(error, showToast);
        });
      return;
    }
    const stopIds = getStopIds(routeDetails);
    let DispatchStopsOnDayInput: DispatchStopsOnDayInput = {
      autoRoutedStopIds: autoRoutedStopIds ? autoRoutedStopIds : [],
      stopIds: stopIds,
      stopDate: selectedStopDate || selectedRow.shipment?.shipmentDate,
      orderingAlgorithmType: selectedOrderingAlgorithmType,
    };
    dispatchDriverForShipmentMultiDayMutation({
      variables: {
        courierId: courierId,
        shipmentId: selectedRow.id,
        driverId: selectedDriver.id,
        orderingAlgorithmType: selectedOrderingAlgorithmType,
        dispatchedBy: employeeId ? employeeId : courieUser!.id,
        dispatchSource: DispatchSource.Dispatcher,
        days: [DispatchStopsOnDayInput],
        dispatchPreviewSortingType,
        dispatchPreviews: dispatchPreviews,
        driverRank,
        dispatchPreviewSection,
        dispatchPageType,
        dispatchScope,
      },
    })
      .then(() => {
        handleSuccessDispatchResponse();
      })
      .catch((error) => {
        shouldScheduleAfterAssignRef.current = false;
        showErrorToast(error, showToast);
      });
  }, [
    selectedRow,
    selectedDriver,
    selectedOrderingAlgorithmType,
    courierId,
    listStopDates,
    daysGrouped,
    routeDetails,
    autoRoutedStopIds,
    selectedStopDate,
    employeeId,
    courieUser,
    dispatchPreviewSortingType,
    dispatchPreviews,
    driverRank,
    dispatchPreviewSection,
    dispatchPageType,
    dispatchScope,
  ]);

  const handleAssignDriver = () => {
    if (
      !selectedRow ||
      !selectedDriver ||
      !selectedOrderingAlgorithmType ||
      !courierId ||
      !selectedRow.shipment?.order?.id
    ) {
      return;
    }

    complianceCheck(
      selectedDriver.id,
      [selectedRow.shipment.order.id],
      executeDispatch,
    );
  };

  const handleClose = () => {
    shouldScheduleAfterAssignRef.current = false;
    setSelectedDriver(undefined);
    setRouteDetails(undefined);
    resetDayState();
  };

  const handleSuccessDispatchResponse = () => {
    const shouldSchedule = shouldScheduleAfterAssignRef.current;
    handleClose();
    resetKeyboardStates && resetKeyboardStates();
    showToast({
      message: "The order is dispatched successfully",
      type: "success",
    });
    if (shouldSchedule && onAssignAndSchedule) {
      // Defer onDispatch refetch — calling it now would reset selectedRow
      // before the Schedule modal can read it, causing the modal to unmount.
      setIsOpen(false);
      onAssignAndSchedule();
    } else {
      onDispatch && onDispatch();
      if (onConfirm) {
        onConfirm(shouldConfigureAllocationRef.current);
      } else {
        setIsOpen(false);
      }
    }
  };

  function groupStopsByDate(
    routeDetails: Stop[] | undefined,
    selectedStopDate: string,
    autoRoutedStopIds: string[] | undefined,
  ): DispatchStopsOnDayInput[] | undefined {
    if (!routeDetails || !selectedOrderingAlgorithmType) return undefined;

    const groupedByDate: { [key: string]: Stop[] } = {};

    routeDetails.forEach((stop) => {
      const date = stop.effectiveStopDate || selectedStopDate;
      if (date) {
        if (!groupedByDate[date]) {
          groupedByDate[date] = [];
        }
        groupedByDate[date].push(stop);
      }
    });

    return Object.entries(groupedByDate).map(([date, stops]) => ({
      autoRoutedStopIds,
      orderingAlgorithmType: selectedOrderingAlgorithmType,
      stopDate: date,
      stopIds: stops.map((stop) => stop.id),
    }));
  }

  const handleAssignOrNext = () => {
    if (isLastStopDate) {
      handleAssignDriver();
    } else {
      const nextStopDateIndex = listStopDates.indexOf(selectedStopDate) + 1;
      setSelectedStopDate(listStopDates[nextStopDateIndex]);
    }
  };

  const modalOnClose = () => {
    handleClose();
    setIsOpen(false);
  };

  if (showWarning) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={modalOnClose}
        size={SIZE.auto}
        autoFocus={false}
      >
        <DriverComplianceWarningContent
          warningTypes={warningTypes}
          onClose={closeWarning}
          onProceed={proceedFromWarning}
        />
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={modalOnClose}
      size={SIZE.auto}
      autoFocus={false}
    >
      <BaseModalHeader>
        {" "}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <DriverAvatar
              driverPhoto={selectedDriver?.photoUrl}
              driverName={selectedDriver?.firstName}
              avatarDisplay={selectedDriver?.avatarDisplay || undefined}
              size={25}
            />
            Assign Task to {selectedDriver?.firstName}
          </div>
          <div>
            <StopDateListTimeline
              listStopDates={listStopDates}
              selectedStopDate={selectedStopDate}
              shipmentDate={memoizedSelectedRow?.shipment?.shipmentDate || ""}
            />
          </div>
          <span className="mr-3"></span>
        </div>{" "}
      </BaseModalHeader>
      <BaseModalBody
        style={{ minWidth: 1200, height: "70vh", overflow: "hidden" }}
      >
        {routeDetails && (
          <ChangeDriverRouteView
            loading={false}
            routeDetails={routeDetails}
            setRouteDetails={setRouteDetails}
            isValidOrder={isValidOrder}
            setIsValidOrder={setIsValidOrder}
            driver={selectedDriver}
            selectedShipment={memoizedSelectedRow?.shipment}
            onAlgorithmTypeChange={setSelectedOrderingAlgorithmType}
            algorithmType={selectedOrderingAlgorithmType}
            routeViewMode={routeViewMode}
            dispatchScope={dispatchScope}
            setAutoRoutedStopIds={setAutoRoutedStopIds}
            setErrorMessage={setErrorMessage}
            selectedStopDate={selectedStopDate}
            setLoading={setIsLoading}
          />
        )}
      </BaseModalBody>
      <BaseModalFooter className="flex justify-between">
        <div className="flex items-center gap-3">
          {listStopDates && listStopDates.length > 1 ? (
            <CourieButton
              color={"secondary"}
              onClick={() => {
                const prevStopDateIndex =
                  listStopDates.indexOf(selectedStopDate) - 1;
                if (prevStopDateIndex >= 0) {
                  setSelectedStopDate(listStopDates[prevStopDateIndex]);
                } else {
                }
              }}
            >
              Back
            </CourieButton>
          ) : (
            <CourieButton color={"secondary"} onClick={modalOnClose}>
              Cancel
            </CourieButton>
          )}
          {isLastStopDate && onAssignAndSchedule && (
            <div className="text-xs text-gray-500">
              <ToggleSwitch
                checked={isScheduleNextStop}
                onChange={setIsScheduleNextStop}
                label="Pre-assign Next Stops"
              />
            </div>
          )}
          {isLastStopDate &&
            routeViewMode === RouteViewMode.ChangeDriver &&
            onConfirm && (
            <div className="text-xs text-gray-500">
              <ToggleSwitch
                checked={shouldConfigureAllocation}
                onChange={setShouldConfigureAllocation}
                label="Configure Allocation"
              />
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {errorMessage && (
            <span className="text-red-600 my-auto pr-5">{errorMessage}</span>
          )}
          <CourieButton
            isProcessing={
              DispatchDriverForShipmentMutationLoading ||
              DispatchDriverForShipmentMultiDayMutationLoading ||
              isLoading ||
              isCheckingCompliance ||
              isWaitingForGroupStopDates
            }
            onClick={() => {
              shouldScheduleAfterAssignRef.current = isScheduleNextStop;
              shouldConfigureAllocationRef.current = shouldConfigureAllocation;
              handleAssignOrNext();
            }}
            disabled={
              !isValidOrder ||
              isLoading ||
              isCheckingCompliance ||
              isWaitingForGroupStopDates ||
              DispatchDriverForShipmentMutationLoading ||
              DispatchDriverForShipmentMultiDayMutationLoading
            }
          >
            {isLastStopDate
              ? `${submitButtonLabel} to ${selectedDriver?.firstName}`
              : "Next"}
          </CourieButton>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}

export default AssignDriverModal;
